import type { LoadNotation, Prescription, SetEntry, ExerciseEntry } from "@coachbrain/contracts";

export type Units = "lb" | "kg";
const KG_PER_LB = 0.45359237;

export function loadDisplay(load: LoadNotation | undefined, units: Units): string {
  if (!load) return "";
  if (load.ambiguous || load.normalizedKg == null) return load.enteredText.replace(/\s*lb$/i, " lb");
  const v = units === "kg" ? load.normalizedKg : load.normalizedKg / KG_PER_LB;
  return `${Math.round(v)}`;
}
export function kgToUnits(kg: number, units: Units): number { return units === "kg" ? Math.round(kg) : Math.round(kg / KG_PER_LB); }

export function setSummary(sets: SetEntry[], units: Units): string {
  const parts = sets.map((s) => {
    if (s.timeSeconds != null) return `${Math.round(s.timeSeconds / 60)} min`;
    if (s.distanceM != null) return `${(s.distanceM / 1000).toFixed(1)} km`;
    const load = s.load ? loadDisplay(s.load, units) : "";
    if (load && s.reps != null) return `${load}×${s.reps}`;
    if (s.reps != null) return `×${s.reps}`;
    return "";
  }).filter(Boolean);
  return parts.join(" · ");
}
export function entrySummary(e: ExerciseEntry, units: Units): string {
  if (e.skipped) return `Skipped${e.skipReason ? ` · ${e.skipReason}` : ""}`;
  return setSummary(e.sets, units);
}

export function prescriptionText(p: Prescription, units: Units): string {
  if (p.timeSeconds != null && (p.sets ?? 1) <= 1) return `${Math.round(p.timeSeconds / 60)} min`;
  const sets = p.sets ?? 0;
  const reps = p.reps != null ? ` × ${p.reps}` : p.timeSeconds != null ? ` × ${Math.round(p.timeSeconds / 60)} min` : "";
  const load = p.loadKg != null ? ` @ ${kgToUnits(p.loadKg, units)}` : p.loadText ? ` @ ${p.loadText}` : "";
  return `${sets}${reps}${load}`;
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "Thu 4 Sep" */
export function dateShort(iso: string): string {
  const d = new Date(iso);
  return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}
/** "4 Sep" */
export function dateDayMonth(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}
export function timeMMSS(seconds: number): string { const m = Math.floor(seconds / 60); const s = seconds % 60; return `${m}:${s.toString().padStart(2, "0")}`; }
export function humanPattern(p: string): string { return p.replace(/_/g, " "); }
